import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Check, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, paid: false, reference: null, error: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState({ loading: false, error: "Missing session_id", paid: false }); return; }
    let stop = false;
    const poll = async () => {
      attempts.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          if (!stop) setState({ loading: false, paid: true, reference: r.data.booking_reference });
          return;
        }
        if (r.data.payment_status === "failed" || r.data.payment_status === "expired") {
          if (!stop) setState({ loading: false, paid: false, error: `Payment ${r.data.payment_status}` });
          return;
        }
      } catch (e) {
        if (attempts.current > 8) { if (!stop) setState({ loading: false, paid: false, error: "Could not verify payment" }); return; }
      }
      if (attempts.current > 20) { if (!stop) setState({ loading: false, paid: false, error: "Payment confirmation is taking longer than expected." }); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  if (state.loading) return (
    <div className="max-w-md mx-auto px-5 py-20 text-center" data-testid="payment-processing">
      <Loader2 className="animate-spin mx-auto text-[var(--fpn-navy)]" size={32} />
      <h1 className="font-display text-2xl font-semibold mt-6 text-[var(--fpn-navy)]">Confirming your payment…</h1>
      <p className="text-sm text-slate-500 mt-2">This usually takes a few seconds.</p>
    </div>
  );

  if (!state.paid) return (
    <div className="max-w-md mx-auto px-5 py-20 text-center" data-testid="payment-error">
      <h1 className="font-display text-2xl font-semibold text-red-600">Payment not completed</h1>
      <p className="text-sm text-slate-500 mt-2">{state.error}</p>
      <Link to="/" className="btn-navy inline-block rounded-full px-6 py-3 text-sm mt-6">Back to home</Link>
    </div>
  );

  return (
    <div className="max-w-md mx-auto px-5 py-20 text-center" data-testid="payment-success">
      <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mx-auto"><Check size={28}/></div>
      <h1 className="font-display text-3xl font-semibold text-[var(--fpn-navy)] mt-6">Payment received.</h1>
      <p className="text-sm text-slate-500 mt-2">Booking reference <span className="font-mono font-semibold" data-testid="success-reference">{state.reference}</span></p>
      <Link to={`/booking/${state.reference}`} className="btn-amber inline-block rounded-full px-6 py-3 text-sm mt-8" data-testid="success-view-booking">View my booking</Link>
    </div>
  );
}
